/**
 * Folders of service types (docs/13-ACCESS.md): by campus, venue or
 * ministry, nested as deep as a church likes (Planning Center sets no limit,
 * and neither do we). A service type sits in at most one folder, or at the
 * root. Pure rules, shared by the API (which enforces them) and the screens
 * (which only offer what the API would accept).
 */

/** What the rules need of a folder. */
export interface FolderLink {
  id: string
  parentId: string | null
  sortOrder: number
  name: string
}

/** A folder in tree order, with how deep it sits (1 = top level). */
export type PlacedFolder<F extends FolderLink> = F & { depth: number }

/** A folder with the folders inside it. */
export type FolderNode<F extends FolderLink> = F & { depth: number; children: FolderNode<F>[] }

const bySiblingOrder = (a: FolderLink, b: FolderLink) =>
  a.sortOrder - b.sortOrder || a.name.localeCompare(b.name) || a.id.localeCompare(b.id)

function childrenOf<F extends FolderLink>(folders: readonly F[]) {
  const ids = new Set(folders.map((folder) => folder.id))
  const children = new Map<string | null, F[]>()
  for (const folder of folders) {
    // A parent that is not in the list (deleted, or another church's) leaves
    // the folder at the top rather than out of sight.
    const parent = folder.parentId !== null && ids.has(folder.parentId) ? folder.parentId : null
    children.set(parent, [...(children.get(parent) ?? []), folder])
  }
  for (const list of children.values()) list.sort(bySiblingOrder)
  return children
}

/**
 * The folders as nested lists: siblings in the church's order (then by
 * name). A folder whose chain loops back on itself — which the rules never
 * allow — is left out rather than recursed into forever.
 */
export function folderTree<F extends FolderLink>(folders: readonly F[]): FolderNode<F>[] {
  const children = childrenOf(folders)
  const seen = new Set<string>()
  const build = (parentId: string | null, depth: number): FolderNode<F>[] =>
    (children.get(parentId) ?? [])
      .filter((folder) => !seen.has(folder.id))
      .map((folder) => {
        seen.add(folder.id)
        return { ...folder, depth, children: build(folder.id, depth + 1) }
      })
  return build(null, 1)
}

/** Every folder in tree order — a folder, then everything inside it — with its depth. */
export function sortFolders<F extends FolderLink>(folders: readonly F[]): PlacedFolder<F>[] {
  const children = childrenOf(folders)
  const seen = new Set<string>()
  const out: PlacedFolder<F>[] = []
  const walk = (parentId: string | null, depth: number) => {
    for (const folder of children.get(parentId) ?? []) {
      if (seen.has(folder.id)) continue
      seen.add(folder.id)
      out.push({ ...folder, depth })
      walk(folder.id, depth + 1)
    }
  }
  walk(null, 1)
  return out
}

/** The folder's ancestors and itself, outermost first; empty for an unknown id. */
export function folderPath<F extends FolderLink>(folders: readonly F[], id: string | null): F[] {
  const byId = new Map(folders.map((folder) => [folder.id, folder]))
  const path: F[] = []
  const seen = new Set<string>()
  let current = id === null ? undefined : byId.get(id)
  while (current && !seen.has(current.id)) {
    seen.add(current.id)
    path.unshift(current)
    current = current.parentId === null ? undefined : byId.get(current.parentId)
  }
  return path
}

/** How deep a folder sits: 1 at the top level, 0 for the root (null) or an unknown id. */
export const folderDepth = (folders: readonly FolderLink[], id: string | null): number =>
  folderPath(folders, id).length

/** Every folder inside this one, at any depth (not the folder itself). */
export function descendantIds(folders: readonly FolderLink[], id: string): Set<string> {
  const children = childrenOf(folders)
  const out = new Set<string>()
  const stack = [...(children.get(id) ?? [])]
  while (stack.length > 0) {
    const folder = stack.pop()
    if (!folder || out.has(folder.id) || folder.id === id) continue
    out.add(folder.id)
    stack.push(...(children.get(folder.id) ?? []))
  }
  return out
}

/**
 * Whether a folder may go into `parentId` (null: the top level). `id` is the
 * folder being moved, or undefined for a new one. Returns why not, in words
 * for the person doing it, or null when it may. Any depth will do; what is
 * refused is a folder that isn't the church's, and a loop (a folder inside
 * itself, or inside anything inside it).
 */
export function folderPlacementError(
  folders: readonly FolderLink[],
  move: { id?: string; parentId: string | null },
): string | null {
  const { id, parentId } = move
  if (parentId !== null && !folders.some((folder) => folder.id === parentId))
    return 'Choose one of your folders.'
  if (id !== undefined && parentId !== null) {
    if (parentId === id) return 'A folder cannot go inside itself.'
    if (descendantIds(folders, id).has(parentId))
      return 'A folder cannot go inside one of its own folders.'
  }
  return null
}

/** The folders a folder could move into: none inside itself. */
export function folderDestinations<F extends FolderLink>(
  folders: readonly F[],
  id?: string,
): PlacedFolder<F>[] {
  return sortFolders(folders).filter(
    (folder) => folderPlacementError(folders, { id, parentId: folder.id }) === null,
  )
}

/** The next place at the end of a folder's subfolders (or the top level). */
export function nextFolderSortOrder(
  folders: readonly FolderLink[],
  parentId: string | null,
): number {
  const siblings = folders.filter((folder) => folder.parentId === parentId)
  return siblings.length === 0 ? 0 : Math.max(...siblings.map((folder) => folder.sortOrder)) + 1
}

/**
 * What deleting a folder does to the folders inside it: they move up to its
 * parent, after the parent's own subfolders, in their own order. The folder's
 * service types move up the same way (to `parentId`).
 */
export function foldersAfterDelete(
  folders: readonly FolderLink[],
  id: string,
): {
  parentId: string | null
  moved: { id: string; parentId: string | null; sortOrder: number }[]
} {
  const folder = folders.find((candidate) => candidate.id === id)
  const parentId = folder?.parentId ?? null
  const remaining = folders.filter((candidate) => candidate.id !== id)
  let next = nextFolderSortOrder(remaining, parentId)
  const moved = folders
    .filter((candidate) => candidate.parentId === id && candidate.id !== id)
    .sort(bySiblingOrder)
    .map((child) => ({ id: child.id, parentId, sortOrder: next++ }))
  return { parentId, moved }
}

/**
 * A new order for one folder's subfolders (or the top level's): it must name
 * each of them exactly once — a stale screen's partial order is refused, not
 * guessed. Returns why not, or null.
 */
export function siblingOrderError(
  folders: readonly FolderLink[],
  parentId: string | null,
  ids: readonly string[],
): string | null {
  const siblings = new Set(
    folders.filter((folder) => folder.parentId === parentId).map((folder) => folder.id),
  )
  const given = new Set(ids)
  if (given.size !== ids.length) return 'Each folder goes in the order once.'
  if (given.size !== siblings.size || ids.some((id) => !siblings.has(id)))
    return 'The folders have changed since this screen loaded. Reload and try again.'
  return null
}

/** A folder as one level of the drill-down shows it: a card with what is inside. */
export interface FolderCard<F extends FolderLink> {
  folder: F
  /** Service types directly inside it. */
  serviceTypes: number
  /** Folders directly inside it (those the level would show). */
  folders: number
  /** Service types anywhere inside it, at any depth. */
  total: number
}

/** One level of the drill-down: where it is, the folders in it, then its service types. */
export interface FolderLevel<F extends FolderLink, T> {
  /** False for a folder that isn't in the list (gone, or not visible): nothing to show. */
  found: boolean
  /** The folder's ancestors and itself, outermost first; empty at the top. */
  path: F[]
  /** Its folders, in the church's order, each with what is inside. */
  folders: FolderCard<F>[]
  /** The service types directly in it (in the order given). */
  types: T[]
}

/**
 * One level of the folders (null: the top), as the Plans page drills into
 * them: the folders directly in it first, then its own service types. A
 * folder with no service types anywhere inside it is left out — someone who
 * sees only one service type deep down sees only the folders leading to it —
 * unless `keepEmpty` says to keep it (an Administrator there, who fills it).
 * At the top, a service type whose folder isn't in the list counts as there.
 */
export function folderLevel<F extends FolderLink, T extends { folderId: string | null }>(
  folders: readonly F[],
  types: readonly T[],
  folderId: string | null,
  keepEmpty: (folder: F) => boolean = () => false,
): FolderLevel<F, T> {
  const path = folderPath(folders, folderId)
  if (folderId !== null && path.length === 0) return { found: false, path, folders: [], types: [] }
  const children = childrenOf(folders)
  const known = new Set(sortFolders(folders).map((folder) => folder.id))
  const direct = new Map<string, number>()
  for (const type of types)
    if (type.folderId !== null && known.has(type.folderId))
      direct.set(type.folderId, (direct.get(type.folderId) ?? 0) + 1)
  // Service types anywhere inside each folder; a loop (bad data) counts nothing twice.
  const totals = new Map<string, number>()
  const total = (id: string, seen: Set<string>): number => {
    const cached = totals.get(id)
    if (cached !== undefined) return cached
    if (seen.has(id)) return 0
    seen.add(id)
    let sum = direct.get(id) ?? 0
    for (const child of children.get(id) ?? []) sum += total(child.id, seen)
    totals.set(id, sum)
    return sum
  }
  const shown = (folder: F) => total(folder.id, new Set()) > 0 || keepEmpty(folder)
  const level = (children.get(folderId) ?? []).filter(shown)
  return {
    found: true,
    path,
    folders: level.map((folder) => ({
      folder,
      serviceTypes: direct.get(folder.id) ?? 0,
      folders: (children.get(folder.id) ?? []).filter(shown).length,
      total: total(folder.id, new Set()),
    })),
    types: types.filter((type) =>
      folderId === null
        ? type.folderId === null || !known.has(type.folderId)
        : type.folderId === folderId,
    ),
  }
}

/** "3 service types · 1 folder", "Empty": what a folder's card says is inside it. */
export function folderContentsLabel(card: { serviceTypes: number; folders: number }): string {
  const parts = [
    card.serviceTypes > 0 &&
      `${card.serviceTypes} service ${card.serviceTypes === 1 ? 'type' : 'types'}`,
    card.folders > 0 && `${card.folders} ${card.folders === 1 ? 'folder' : 'folders'}`,
  ].filter((part) => part !== false)
  return parts.length > 0 ? parts.join(' · ') : 'Empty'
}

/** A place access can be given (docs/13-ACCESS.md): the church, a folder or a service type. */
export interface AccessPlace {
  kind: 'org' | 'folder' | 'service_type'
  /** null for the church. */
  id: string | null
  name: string
  /** How far in it sits: 0 the church, 1 a top-level folder or a root service type. */
  depth: number
}

/**
 * Every place access can be given, as an indented list reads: the church;
 * the service types at the root; then each folder in tree order with its own
 * service types under it. A service type whose folder is not in the list
 * (not visible) sits at the root.
 */
export function accessPlaces<
  F extends FolderLink,
  T extends { id: string; name: string; folderId: string | null },
>(folders: readonly F[], types: readonly T[], churchName = 'Church-wide'): AccessPlace[] {
  const placed = sortFolders(folders)
  const known = new Set(placed.map((folder) => folder.id))
  const typesIn = (folderId: string | null) =>
    types.filter((type) =>
      folderId === null
        ? type.folderId === null || !known.has(type.folderId)
        : type.folderId === folderId,
    )
  const out: AccessPlace[] = [{ kind: 'org', id: null, name: churchName, depth: 0 }]
  for (const type of typesIn(null))
    out.push({ kind: 'service_type', id: type.id, name: type.name, depth: 1 })
  for (const folder of placed) {
    out.push({ kind: 'folder', id: folder.id, name: folder.name, depth: folder.depth })
    for (const type of typesIn(folder.id))
      out.push({ kind: 'service_type', id: type.id, name: type.name, depth: folder.depth + 1 })
  }
  return out
}
